<template>
  <div class="wc-checkin-pool" :class="isDarkMode ? 'theme--dark' : 'theme--light'">
    <div class="px-3 pb-2 pt-1">
      <v-text-field
        v-model="search"
        placeholder="Suchen..."
        dense
        solo
        flat
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
        style="border-radius: 8px"
        class="wc-cp-search"
        @click:clear="search = ''" />
    </div>

    <div v-if="loading && !pool.length" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="28" />
    </div>

    <div v-else-if="!loading && !filteredPool.length" class="text-center py-8 opacity-40">
      <v-icon size="36" color="grey">mdi-account-off-outline</v-icon>
      <div class="caption mt-2 var-text-muted">Keine Check-Ins gefunden</div>
    </div>

    <div v-else class="wc-cp-list">
      <div v-for="entry in filteredPool" :key="entry._id" class="wc-cp-row" @click="selectEntry(entry)">
        <v-badge :color="isCheckedIn(entry) ? 'green accent-4' : 'red lighten-2'" dot bordered overlap offset-x="8" offset-y="8">
          <v-avatar size="40">
            <img v-if="getPicture(entry)" :src="getPicture(entry)" alt="Profilbild" loading="lazy" />
            <span v-else class="var-text-main text-subtitle-2">{{ getInitials(entry) }}</span>
          </v-avatar>
        </v-badge>

        <div class="wc-cp-info ml-3">
          <div class="wc-cp-name var-text-main">
            {{ entry.firstName }} {{ entry.lastName }}
            <v-icon v-if="isBirthday(entry)" x-small color="amber" class="ml-1">mdi-cake-variant</v-icon>
          </div>
          <div class="wc-cp-email var-text-muted">{{ entry.email || "Keine E-Mail" }}</div>
        </div>

        <div class="wc-cp-time ml-auto flex-shrink-0">
          <v-chip x-small color="rgba(76,175,80,0.2)" style="border: 1px solid rgba(76, 175, 80, 0.4); color: #81c784">
            <v-icon x-small class="mr-1" color="#81c784">mdi-login</v-icon>
            {{ formatTime(entry.checkinDate) }}
          </v-chip>
        </div>

        <v-icon x-small class="ml-2 flex-shrink-0" style="color: var(--icon-muted)">mdi-chevron-right</v-icon>
      </div>
    </div>

    <div v-if="pool.length < totalEntries && !loading" class="text-center py-3">
      <v-btn x-small text color="primary" @click="loadMore">Mehr laden</v-btn>
    </div>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "WorkCalendarCheckinPool",

  data() {
    return {
      pool: [],
      search: "",
      loading: false,
      page: 1,
      itemsPerPage: 30,
      totalEntries: 0,
      searchTimeout: null,
    }
  },

  computed: {
    isDarkMode() {
      return localStorage.getItem("wc_isDarkMode") !== "false"
    },
    filteredPool() {
      if (!this.search) return this.pool
      const q = this.search.toLowerCase().trim()
      return this.pool.filter((e) => {
        const name = `${e.firstName} ${e.lastName}`.toLowerCase()
        const email = (e.email || "").toLowerCase()
        return name.includes(q) || email.includes(q)
      })
    },
  },

  watch: {
    search() {
      // Debounced server search for larger datasets
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.fetchPool(true)
      }, 300)
    },
  },

  created() {
    this.fetchPool(true)
  },

  beforeDestroy() {
    clearTimeout(this.searchTimeout)
  },

  methods: {
    async fetchPool(reset = false) {
      if (this.loading) return
      if (reset) {
        this.page = 1
        this.pool = []
      }
      this.loading = true
      try {
        const params = {
          page: this.page,
          limit: this.itemsPerPage,
          search: this.search,
          filterCheckedout: true,
        }
        const { entries, total } = await api.fetchCheckinPoolNew(params)
        this.pool.push(...(entries || []))
        this.totalEntries = total || 0
      } catch (e) {
        console.error("Fehler beim Laden der Check-Ins:", e)
      } finally {
        this.loading = false
      }
    },

    loadMore() {
      if (this.pool.length >= this.totalEntries) return
      this.page++
      this.fetchPool(false)
    },

    selectEntry(entry) {
      if (!entry.user || !entry.user._id) {
        this.$toast?.warning("Dieser Gast hat keinen verknüpften Account.")
        return
      }
      this.$emit("entry-selected", entry)
    },

    getPicture(entry) {
      if (entry.user?.bild) {
        const webp = entry.user.bild.replace(/\.[^/.]+$/, ".webp")
        return `/img/profilbilder/${webp}`
      }
      if (entry.user?.tac_image) {
        return `data:image/jpeg;base64,${entry.user.tac_image}`
      }
      return null
    },

    getInitials(entry) {
      return `${(entry.firstName || "")[0] || ""}${(entry.lastName || "")[0] || ""}`.toUpperCase()
    },

    isCheckedIn(entry) {
      return !entry.checkoutDate || new Date(entry.checkoutDate) < new Date(entry.checkinDate)
    },

    isBirthday(entry) {
      if (!entry.birthday) return false
      const now = new Date()
      const bd = new Date(entry.birthday)
      return now.getDate() === bd.getDate() && now.getMonth() === bd.getMonth()
    },

    formatTime(dateString) {
      if (!dateString) return ""
      return new Date(dateString).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
  },
}
</script>

<style scoped>
.wc-checkin-pool {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.wc-cp-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 8px;
}

.wc-cp-row {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 12px;
  cursor: pointer;
  margin-bottom: 4px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.15s ease;
}

.wc-cp-row:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(76, 175, 80, 0.3);
  transform: translateY(-1px);
}

.wc-cp-row:active {
  transform: scale(0.98);
}

.wc-cp-info {
  flex: 1;
  min-width: 0;
}

.wc-cp-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wc-cp-email {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}

.wc-cp-time {
  text-align: right;
}

.wc-cp-search >>> .v-input__slot {
  min-height: 32px !important;
  border-radius: 8px !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.wc-cp-search >>> input {
  font-size: 0.8rem;
}

.wc-cp-search >>> .v-icon {
  font-size: 16px !important;
  color: rgba(255, 255, 255, 0.3) !important;
}
.var-text-main {
  color: var(--text-main, #ffffff) !important;
}
.var-text-muted {
  color: var(--text-muted, rgba(255, 255, 255, 0.4)) !important;
}
.wc-cp-row {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 12px;
  cursor: pointer;
  margin-bottom: 4px;
  background: var(--bg-panel, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--border-light, rgba(255, 255, 255, 0.06));
  transition: all 0.15s ease;
}
.wc-cp-row:hover {
  background: var(--bg-panel-hover, rgba(255, 255, 255, 0.07));
  border-color: rgba(76, 175, 80, 0.3);
  transform: translateY(-1px);
}
.theme--light .wc-cp-name,
.theme--light .var-text-main {
  color: rgba(0, 0, 0, 0.87) !important;
}
.theme--light .wc-cp-email,
.theme--light .var-text-muted {
  color: rgba(0, 0, 0, 0.54) !important;
}
.theme--light .wc-cp-row {
  background: rgba(0, 0, 0, 0.02);
  border-color: rgba(0, 0, 0, 0.06);
}
.theme--light .wc-cp-row:hover {
  background: rgba(0, 0, 0, 0.04);
}
</style>
